import config from "@/env.config";
import { LearningClassEnums as CLS } from "@/ts/enums/LearningClassEnums";
import { openUsersnapWidget } from "@/utils/usersnap";
import {
  Box,
  Flex,
  Float,
  Heading,
  Image,
  Link,
  Portal,
  Text
} from "@chakra-ui/react";
import {
  GradeTheme,
  NavigationIcons,
  Tooltip,
  getTrackingId
} from "@euka-mfes/bark";
import { useAppConfigSelector, useUserSelector } from "@context/hooks";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import primaryHelpBtnHover from "@assets/usersnap/eko-help-btn-primary-hover.webp";
import primaryHelpBtn from "@assets/usersnap/eko-help-btn-primary.webp";
import secondaryHelpBtnHover from "@assets/usersnap/eko-help-btn-secondary-hover.webp";
import secondaryHelpBtn from "@assets/usersnap/eko-help-btn-secondary.webp";
import seniorHelpBtnHover from "@assets/usersnap/eko-help-btn-senior-hover.webp";
import seniorHelpBtn from "@assets/usersnap/eko-help-btn-senior.webp";

interface HelpOption {
  id: string;
  line1: string;
  line2: string;
  action: () => void;
}

// Helper function to get the correct image based on grade theme and state
const getHelpButtonImage = (
  gradeTheme: GradeTheme,
  isActive: boolean
): string => {
  const imageMap: Record<GradeTheme, { normal: string; hover: string }> = {
    [GradeTheme.Primary]: {
      normal: primaryHelpBtn,
      hover: primaryHelpBtnHover
    },
    [GradeTheme.Secondary]: {
      normal: secondaryHelpBtn,
      hover: secondaryHelpBtnHover
    },
    [GradeTheme.Senior]: {
      normal: seniorHelpBtn,
      hover: seniorHelpBtnHover
    }
  };

  return isActive ? imageMap[gradeTheme].hover : imageMap[gradeTheme]?.normal;
};


const HelpButton = () => {
  const { pathname } = useLocation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFooter, setHasFooter] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const { headerDetails } = useUserSelector();
  const { gradeTheme } = useAppConfigSelector();

  const firstName = headerDetails?.student?.first_name;
  const eukaId = headerDetails?.student?.username;
  const parentEmail = headerDetails?.student?.parent_email;

  // Usersnap widget IDs from environment config
  const { USERSNAP_REPORT_ISSUE_WIDGET_ID } = config;
  const { USERSNAP_SUGGEST_FEEDBACK_WIDGET_ID } = config;

  // Determine if the button should show the active (hover) state
  const isActive = isHovered || isPopupOpen;
  const buttonImage = getHelpButtonImage(gradeTheme, isActive);

  const helpOptions: HelpOption[] = [
    {
      id: "report-issue",
      line1: "Something broken?",
      line2: "Report an issue",
      action: () => {
        openUsersnapWidget(
          String(USERSNAP_REPORT_ISSUE_WIDGET_ID),
          eukaId ?? "",
          parentEmail ?? ""
        );
        setIsPopupOpen(false);
      }
    },
    {
      id: "suggest-feedback",
      line1: "Have an idea?",
      line2: "Let me know how Euka can improve",
      action: () => {
        openUsersnapWidget(
          String(USERSNAP_SUGGEST_FEEDBACK_WIDGET_ID),
          eukaId ?? "",
          parentEmail ?? ""
        );
        setIsPopupOpen(false);
      }
    },
    {
      id: "help-center",
      line1: "Something else?",
      line2: "Check out the Euka help centre",
      action: () => {
        window.open("https://help.euka.edu.au/en/", "_blank");
        setIsPopupOpen(false);
      }
    }
  ];

  // Check if footer exists in the DOM to ensure HelpButton does not cover the footer or page content,
  // adjuest padding accordingly.
  useEffect(() => {
    const checkFooter = () => {
      const footerElement = document.querySelector(
        "[data-testid='footer-line']"
      );
      setHasFooter(!!footerElement);
    };

    checkFooter();

    const observer = new MutationObserver(checkFooter);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  // Hide the default Usersnap button on mount
  useEffect(() => {
    if (window.Usersnap && typeof window.Usersnap.hide === "function") {
      try {
        window.Usersnap.hide(String(USERSNAP_REPORT_ISSUE_WIDGET_ID));
        window.Usersnap.hide(String(USERSNAP_SUGGEST_FEEDBACK_WIDGET_ID));
      } catch (error) {
        console.warn("Failed to hide Usersnap widgets:", error);
      }
    }
  }, [USERSNAP_REPORT_ISSUE_WIDGET_ID, USERSNAP_SUGGEST_FEEDBACK_WIDGET_ID]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsPopupOpen(false);
      }
    };

    if (isPopupOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopupOpen]);

  const handleButtonClick = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  if (pathname.includes("/orientation")) {
    return null;
  }

  return (
    <>
      <Tooltip
        content="Need some help?"
        placement="left"
        openDelay={300}
        closeDelay={100}
      >
        <Box
          className="print-hide"
          ref={buttonRef}
          mb={{ base: hasFooter ? 4 : -2, md: 0 }}
          mr={{ base: hasFooter ? 0 : -2, md: 0 }}
          position="fixed"
          bottom="24px"
          right="24px"
          cursor="pointer"
          onClick={handleButtonClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          zIndex="tooltip"
          data-testid="help-button"
          data-sidebar-persistent="true"
          data-tracking-id={getTrackingId([CLS.HelpButton, CLS.Button], "open")}
        >
          <Image
            src={buttonImage}
            alt="Help button"
            w="94px"
            h="94px"
            filter="drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))"
          />
        </Box>
      </Tooltip>

      {isPopupOpen && (
        <Portal>
          <Box
            className="print-hide light"
            ref={popupRef}
            position="fixed"
            bottom={{ base: 36, md: 32, lg: 32 }}
            right="16px"
            bg="warning.600"
            borderRadius="lg"
            boxShadow="xl"
            p={6}
            minW="343px"
            w={["92vw", "343px"]}
            zIndex="tooltip"
            border="1px solid"
            borderColor="lines"
            data-testid="help-popup"
            data-sidebar-persistent="true"
          >
            {/* Arrow pointing to the help button */}
            <Float placement="bottom-end" offsetX="6" offsetY="-1">
              <Box
                w="0"
                h="0"
                top="1.5rem"
                left="-0.675rem"
                borderLeft="12px solid transparent"
                borderRight="12px solid transparent"
                borderTop="12px solid"
                borderTopColor="warning.600"
              />
            </Float>

            {/* Close button */}
            <Flex justify="space-between" align="start" mb={2}>
              <Heading
                as="h4"
                textStyle="title.2"
                fontSize="18px"
                fontWeight="semibold"
              >
                Hey {firstName}, I'm here to help
              </Heading>

              <Box
                onClick={() => setIsPopupOpen(false)}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}
                data-testid="close-popup-button"
                data-tracking-id={getTrackingId(
                  [CLS.HelpButton, CLS.Button],
                  "close"
                )}
              >
                <NavigationIcons.Cross size="lg" />
              </Box>
            </Flex>
            <Text textStyle="body.standard" mb={4}>
              Select a topic to get the right support:
            </Text>

            <Flex direction="column" gap={3}>
              {helpOptions.map(option => (
                <Box
                  key={option.id}
                  onClick={() => option.action()}
                  cursor="pointer"
                  borderWidth="1px"
                  borderColor="lines"
                  bg="canvas"
                  borderRadius="md"
                  py={3}
                  px={4}
                  data-testid={`help-option-${option.id}`}
                  data-tracking-id={getTrackingId(
                    [CLS.HelpButton, CLS.Link],
                    `option-${option.id}`
                  )}
                >
                  <Text
                    textStyle="body.standard"
                    fontWeight="semibold"
                  >
                    {option.line1}
                  </Text>
                  <Link
                    textStyle="body.standard"
                    color="action.600"
                    textDecoration="none"
                    _hover={{ opacity: 0.8 }}
                  >
                    {option.line2}
                  </Link>
                </Box>
              ))}
            </Flex>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default HelpButton;
